import { directUploadFile } from "../Uploader/directUploadFile";
import { useUploadStepMessages } from "../Uploader/useUploadStepMessages";
import { UPLOAD_STATE_TRANSFERRING } from "../Uploader/directUploadFile";

const UPLOAD_STATUS_ELEMENT_CLASS = "wysiwyg-upload-status";

// Finds the TinyMCE busy spinner overlay and injects a status paragraph into it.
// The spinner is only present in the DOM while an async dialog operation is running,
// so this must be called after the upload pipeline has started.
const injectStatusElement = () => {
  const spinnerEl = document.querySelector(".tox-dialog__busy-spinner");

  if (!spinnerEl) return null;

  const statusEl = document.createElement("p");
  statusEl.className = UPLOAD_STATUS_ELEMENT_CLASS;
  spinnerEl.appendChild(statusEl);

  return statusEl;
};

// Hook that creates a TinyMCE-compatible image upload handler with step-aware
// progress messages injected directly into the dialog's busy spinner overlay.
export const useImageUploadHandler = ({
  onFileUploaded = null,
  onStepChange = null,
  ...args
} = {}) => {
  const getStepMessage = useUploadStepMessages();

  const imagesUploadHandler = (blobInfo, onProgress) => {
    let statusEl = null;
    let currentStepId = null;
    let currentStepLabel = null;

    const updateStatusElement = progress => {
      if (!statusEl || !currentStepLabel) return;

      // Only show the progress percentage during the transfer step, where it
      // reflects meaningful byte-level progress. Other steps have no granular
      // progress to display.
      const showProgress = currentStepId === UPLOAD_STATE_TRANSFERRING;
      const text = showProgress
        ? `${currentStepLabel} (${Math.round(progress)}%)`
        : currentStepLabel;

      statusEl.textContent = text;
    };

    const removeStatusElement = () => {
      if (!statusEl) return;

      statusEl.remove();
      statusEl = null;
    };

    const handleStepChange = stepId => {
      currentStepId = stepId;
      currentStepLabel = getStepMessage(stepId);

      // Inject on the first step — the busy spinner may not exist before this.
      if (!statusEl) statusEl = injectStatusElement();

      // Progress starts at 0 for each step; XHR ticks via handleProgress will
      // update the displayed value during TRANSFERRING.
      updateStatusElement(0);

      // Forward to external consumers with the already-translated label so
      // they don't need to depend on the step message hook themselves.
      if (onStepChange) onStepChange(stepId, currentStepLabel);
    };

    // Intercept onProgress to keep the status element in sync with XHR ticks
    // while still feeding TinyMCE's native progress bar.
    const handleProgress = progress => {
      onProgress(progress);
      updateStatusElement(progress);
    };

    return new Promise((resolve, reject) => {
      directUploadFile({
        file: blobInfo.blob(),
        onProgress: handleProgress,
        onStepChange: handleStepChange,
        onError: error => {
          removeStatusElement();
          reject(error);
        },
        onComplete: data => {
          removeStatusElement();
          if (onFileUploaded) onFileUploaded(data);
          resolve(data.url);
        },
        ...args
      });
    });
  };

  return { imagesUploadHandler };
};
